import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  UsersIcon, 
  AcademicCapIcon, 
  CurrencyDollarIcon, 
  QuestionMarkCircleIcon, 
  UserGroupIcon,
  CashIcon,
  DeviceMobileIcon,
  DesktopComputerIcon,
  ClockIcon,
  ChatIcon,
  BookOpenIcon,
  CreditCardIcon
} from '@heroicons/react/outline';
import { DeviceMobileIcon as DevicePhoneMobileIcon } from '@heroicons/react/outline';
import PageTitle from '../common/PageTitle';
import { usePermissions } from '../../contexts/PermissionsContext';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar
} from 'recharts';
import dashboardService from '../../services/dashboardService';
import { toastService } from '../../services/toast';

function DashboardContent() {
  const { hasPermission } = usePermissions();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // بيانات مؤقتة للرسم البياني - ستأتي من API في التطبيق النهائي
  const chartData = [
    { day: 'السبت', sales: 4500, newUsers: 25 },
    { day: 'الأحد', sales: 6700, newUsers: 32 },
    { day: 'الإثنين', sales: 5400, newUsers: 18 },
    { day: 'الثلاثاء', sales: 7800, newUsers: 27 },
    { day: 'الأربعاء', sales: 9200, newUsers: 42 },
    { day: 'الخميس', sales: 8100, newUsers: 36 },
    { day: 'الجمعة', sales: 4300, newUsers: 24 }
  ];
  
  // جلب بيانات لوحة التحكم من API
  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        // استدعاء خدمة API الحقيقية
        const data = await dashboardService.getDashboardData();
        setDashboardData(data);
        setError(null);
      } catch (err) {
        // خطأ في جلب بيانات لوحة التحكم
        setError('حدث خطأ أثناء جلب بيانات لوحة التحكم');
        toastService.error('حدث خطأ أثناء جلب بيانات لوحة التحكم');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);
  
  // استخدام البيانات الفعلية من API
  const statsCards = [
    {
      title: 'إجمالي المستخدمين',
      value: dashboardData?.stats?.total_users !== undefined ? dashboardData.stats.total_users.toLocaleString() : '-',
      icon: UsersIcon,
      textColor: 'text-blue-600',
      darkTextColor: 'dark:text-blue-400',
      bgColor: 'bg-blue-100',
      darkBgColor: 'dark:bg-blue-900/20',
      requiresPermission: 'view_financial_statistics'
    },
    {
      title: 'الدورات النشطة',
      value: dashboardData?.stats?.active_courses !== undefined ? dashboardData.stats.active_courses.toLocaleString() : '-',
      icon: AcademicCapIcon,
      textColor: 'text-green-600',
      darkTextColor: 'dark:text-green-400',
      bgColor: 'bg-green-100',
      darkBgColor: 'dark:bg-green-900/20',
      requiresPermission: 'view_financial_statistics'
    },
    {
      title: 'إجمالي المبيعات',
      value: dashboardData?.stats?.total_sales !== undefined ? `${dashboardData.stats.total_sales.toLocaleString()} ريال` : '-',
      icon: CurrencyDollarIcon,
      textColor: 'text-purple-600',
      darkTextColor: 'dark:text-purple-400',
      bgColor: 'bg-purple-100',
      darkBgColor: 'dark:bg-purple-900/20',
      requiresPermission: 'view_financial_statistics'
    },
    {
      title: 'الأجهزة النشطة',
      mainValue: dashboardData?.stats?.active_pc_users !== undefined ? dashboardData.stats.active_pc_users.toLocaleString() : '-',
      mainLabel: 'كمبيوتر',
      icon: DesktopComputerIcon,
      secondaryValues: [
        {
          value: dashboardData?.stats?.active_mobile_users !== undefined ? dashboardData.stats.active_mobile_users.toLocaleString() : '-',
          label: 'موبايل',
          icon: DevicePhoneMobileIcon
        }
      ],
      textColor: 'text-indigo-600',
      darkTextColor: 'dark:text-indigo-400',
      bgColor: 'bg-indigo-100',
      darkBgColor: 'dark:bg-indigo-900/20',
      requiresPermission: 'view_financial_statistics'
    }
  ];

  const quickActions = [
    {
      title: 'اسأل مدرب',
      description: 'تواصل مباشرة مع المدربين',
      icon: QuestionMarkCircleIcon,
      bgColor: 'bg-indigo-100',
      darkBgColor: 'dark:bg-indigo-900/20',
      textColor: 'text-indigo-600',
      darkTextColor: 'dark:text-indigo-400',
      link: '/admin/dashboard/ask-instructor'
    },
    {
      title: 'المستخدمين الجدد',
      description: 'عرض آخر المستخدمين المسجلين',
      icon: UserGroupIcon,
      bgColor: 'bg-green-100',
      darkBgColor: 'dark:bg-green-900/20',
      textColor: 'text-green-600',
      darkTextColor: 'dark:text-green-400',
      link: '/admin/dashboard/users/trainees',
      requiresPermission: 'view_financial_statistics'
    },
    {
      title: 'الدورات الأخيرة',
      description: 'عرض آخر الدورات المضافة',
      icon: AcademicCapIcon,
      bgColor: 'bg-blue-100',
      darkBgColor: 'dark:bg-blue-900/20',
      textColor: 'text-blue-600',
      darkTextColor: 'dark:text-blue-400',
      link: '/admin/dashboard/courses',
      requiresPermission: 'view_financial_statistics'
    },
    {
      title: 'المدفوعات الأخيرة',
      description: 'عرض آخر المدفوعات',
      icon: CashIcon,
      bgColor: 'bg-purple-100',
      darkBgColor: 'dark:bg-purple-900/20',
      requiresPermission: 'view_bank_transfers',
      textColor: 'text-purple-600',
      darkTextColor: 'dark:text-purple-400',
      link: '/admin/dashboard/payments/bank-transfers'
    }
  ];

  // تم إزالة متغير recentActivity لأننا لم نعد نستخدمه

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="dashboard-content p-6"
    >
      <PageTitle title="لوحة التحكم" />
      {/* إحصائيات */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {loading ? (
          // مؤشر تحميل للبطاقات الإحصائية
          [...Array(4)].map((_, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0.5 }}
              animate={{ opacity: [0.5, 0.8, 0.5] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 h-32"
            >
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-4"></div>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
              </div>
            </motion.div>
          ))
        ) : (
          // البطاقات الإحصائية
          statsCards.map((stat, index) => {
            // التحقق من الصلاحيات قبل عرض البطاقة
            if (stat.requiresPermission && !hasPermission(stat.requiresPermission)) {
              return null;
            }
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 dark:text-gray-400">{stat.title}</p>
                    {stat.mainValue ? (
                      <>
                        <div className="flex items-center mt-2">
                          {stat.icon && <stat.icon className={`h-4 w-4 ${stat.textColor} ${stat.darkTextColor} ml-1`} />}
                          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                            {stat.mainValue} {stat.mainLabel}
                          </h3>
                        </div>
                        <div className="flex items-center mt-2 space-x-4 space-x-reverse">
                          {stat.secondaryValues?.map((secondary, idx) => (
                            <div key={idx} className="flex items-center">
                              <secondary.icon className={`h-4 w-4 ${stat.textColor} ${stat.darkTextColor} ml-1`} />
                              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                {secondary.value} {secondary.label}
                              </span>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <h3 className="text-2xl font-semibold mt-2 text-gray-900 dark:text-white">
                        {stat.value}
                      </h3>
                    )}
                  </div>
                  <div className={`${stat.bgColor} ${stat.darkBgColor} p-3 rounded-lg`}>
                    <stat.icon className={`h-6 w-6 ${stat.textColor} ${stat.darkTextColor}`} />
                  </div>
                </div>
              </motion.div>
            );
          })
        )}
      </div>

      {/* الإجراءات السريعة */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">الإجراءات السريعة</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {quickActions.map((action, index) => {
            // التحقق من الصلاحيات قبل عرض الإجراء السريع
            if (action.requiresPermission && !hasPermission(action.requiresPermission)) {
              return null;
            }
            
            return (
            <Link 
              key={index} 
              to={action.link}
              className="block"
            >
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm text-right hover:shadow-md transition-all h-full"
              >
                <div className={`inline-flex p-3 rounded-lg ${action.bgColor} ${action.darkBgColor}`}>
                  <action.icon className={`h-6 w-6 ${action.textColor} ${action.darkTextColor}`} />
                </div>
                <h3 className="mt-4 font-semibold text-gray-900 dark:text-white">{action.title}</h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{action.description}</p>
              </motion.div>
            </Link>
          );
          })}
        </div>
      </div>

      {/* الرسم البياني للمبيعات والمستخدمين الجدد */}
      {hasPermission('view_financial_statistics') && (
        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">إحصائيات الأسبوع</h2>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 dark:text-white">المبيعات والمستخدمين الجدد</h3>
              <div className="flex space-x-2 rtl:space-x-reverse">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400">
                  <CurrencyDollarIcon className="h-4 w-4 ml-1" />
                  المبيعات
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400">
                  <UsersIcon className="h-4 w-4 ml-1" />
                  المستخدمين الجدد
                </span>
              </div>
            </div>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dashboardData?.chart || chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.1}/>
                    </linearGradient>
                    <linearGradient id="colorNewUsers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
                  <XAxis dataKey="name" stroke="#6B7280" />
                  <YAxis yAxisId="left" stroke="#3B82F6" />
                  <YAxis yAxisId="right" orientation="right" stroke="#10B981" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(17, 24, 39, 0.8)', 
                      color: '#F9FAFB',
                      border: 'none',
                      borderRadius: '0.5rem',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                    }}
                    itemStyle={{ color: '#F9FAFB' }}
                    labelStyle={{ color: '#F9FAFB', fontWeight: 'bold', marginBottom: '0.5rem' }}
                  />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="sales" 
                    name="المبيعات" 
                    stroke="#3B82F6" 
                    fillOpacity={1} 
                    fill="url(#colorSales)" 
                    yAxisId="left"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="users" 
                    name="المستخدمين الجدد" 
                    stroke="#10B981" 
                    fillOpacity={1} 
                    fill="url(#colorNewUsers)" 
                    yAxisId="right"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
      
    </motion.div>
  );
}

export default DashboardContent;
